// The overlay's pure text helpers, lifted out of overlay.ts (R104).
//
// overlay.ts is a DOM class, and most of it is not worth pinning. These are
// pure string functions: they carry the rules that are easy to get quietly
// wrong. They live here so a test can reach them without constructing an
// overlay, and so the arcade, the hub and the replay write one form.

// The ramp and not the layer (R431): `render/heatmap-ramp.ts` is the pure
// leaf, and this module is chrome that pulls in no renderer.
import { HEAT_BUCKETS, bucketPaint } from '../render/heatmap-ramp';
import { channels } from '../render/color';
import { type GameState, type HeatMode, RESOURCE_NAMES, dur, num } from '../state';
import { AnchorState, HeatmapKind, ObjKind, SignatureOrder } from '../gen/proto/aquaphobia';
import { depthHeatCfg } from '../depthlook';
import type { ChartReading } from '../fast/chart/context';
import { LOCALE, format as S, notices as N, overlay as OS } from '../gen/strings';
import type { TimerClaim } from './notices';
import { GROUP_MARK, groupThousands } from '../locale/group';
import { mmss, wholeSec } from '../locale/clock';
import { leftSpan } from '../locale/span';
import { pad2 } from '../locale/stamp';
import { clamp01 } from '../render/math';

/** A share as the whole percent a benefit sentence prints. */
const benefitPct = (x: number): string => String(Math.round(x * 100));

/** What holding a strategic object gives, in words (numbers.md §8). The
 *  figures ride SiegeInfo.benefit and benefit2, and the unit follows the
 *  kind, so the client computes no rule. */
export function benefitText(kind: number, a: number, b: number): string {
  switch (kind) {
    case ObjKind.OBJ_KIND_THRONE: return S.benefit_throne({ pts: num(a), resist: num(b) });
    case ObjKind.OBJ_KIND_SPIRE: return S.benefit_spire({ px: num(a) });
    case ObjKind.OBJ_KIND_FORGE: return S.benefit_forge({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_KELP: return S.benefit_kelp({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_BATTERY: return S.benefit_battery;
    case ObjKind.OBJ_KIND_DRYDOCK: return S.benefit_drydock({ repair: benefitPct(a), hull: benefitPct(b) });
    case ObjKind.OBJ_KIND_ARCHIVE: return S.benefit_archive({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_MARSHAL: return S.benefit_marshal({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_WARD: return S.benefit_ward({ px: num(a) });
  }
  return '';
}

/** The short form of `benefitText` for a card fact row, which must not cut off. */
export function benefitShort(kind: number, a: number, b: number): string {
  switch (kind) {
    case ObjKind.OBJ_KIND_THRONE: return S.benefit_short_throne({ pts: num(a) });
    case ObjKind.OBJ_KIND_SPIRE: return S.benefit_short_spire({ px: num(a) });
    case ObjKind.OBJ_KIND_FORGE: return S.benefit_short_forge({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_KELP: return S.benefit_short_kelp({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_BATTERY: return S.benefit_short_battery;
    case ObjKind.OBJ_KIND_DRYDOCK: return S.benefit_short_drydock({ repair: benefitPct(a) });
    case ObjKind.OBJ_KIND_ARCHIVE: return S.benefit_short_archive({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_MARSHAL: return S.benefit_short_marshal({ pct: benefitPct(a) });
    case ObjKind.OBJ_KIND_WARD: return S.benefit_short_ward({ px: num(a) });
  }
  return '';
}

/**
 * The band a hull percentage falls in (R425).
 *
 * The map paints the hull bar in the traffic light and the arcade tooltip
 * paints the same lane in the DOM, so the two thresholds have one owner
 * here. The names are the chrome's own token names: `good` is the green at
 * 66% and over, `gold` the amber from 33%, and `bad` the red below it. The
 * map reads them through `HULL_BAND_COLOR`, and the DOM writes them as
 * classes; the game sheet paints `.gold` in `var(--amber)`, because gold
 * is never a signal (R1798).
 */
export function hullBand(pct: number): 'good' | 'gold' | 'bad' {
  return pct >= 66 ? 'good' : pct >= 33 ? 'gold' : 'bad';
}

/** A faction's colour as a CSS hex string: `factionInk` of the labs'
 *  leaf, under the name the chrome reads. */
export { factionInk as cssColor } from '../labkit/ink';

/**
 * A heatmap ramp as a CSS gradient (v13, R259): one stop per bucket, each
 * one the colour and the alpha the canvas layer fills that bucket with,
 * over the water the map shows through.
 *
 * The legend must be the ramp, not an approximation of it. So this reads
 * `bucketPaint` of `render/heatmap-ramp.ts` — the ramp the heat shader
 * transcribes (`fast/shaders/heatmap.ts`) — rather
 * than rebuilding the ramp from the mode's numbers. **The import of a render
 * module by a ui module is deliberate**: the alternative is a second copy of
 * the ramp arithmetic here, and that copy is exactly what went wrong. A
 * ramp that climbed in colour as well as in alpha (R258) left the two-stop
 * legend showing a brighter faint end than the field drew.
 *
 * A mode with no dark endpoint and no alpha span of its own gives twelve
 * stops of one hue at evenly spaced alphas, which paints what the two-stop
 * gradient painted before.
 */
export function rampCss(mode: HeatMode, buckets = HEAT_BUCKETS): string {
  // The DEPTH heat map paints four bands, not a bucket ramp (R2870): its
  // ramp is the four band inks of the depth heat look, a hard step each.
  if (mode.kind === HeatmapKind.HEATMAP_KIND_DEPTH) return depthHeatRampCss();
  const stops: string[] = [];
  for (let b = 0; b < buckets; b++) {
    const { color, alpha } = bucketPaint(mode, b, buckets);
    const rgb = channels(color).join(', ');
    const at = buckets <= 1 ? 100 : (b / (buckets - 1)) * 100;
    stops.push(`rgba(${rgb}, ${+alpha.toFixed(4)}) ${+at.toFixed(2)}%`);
  }
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/** The ramp of the DEPTH heat map: the four band inks of the depth heat
 *  look at their alphas, shelf to abyss, a hard step between two bands. */
function depthHeatRampCss(reading: ChartReading = 'modern'): string {
  const cfg = depthHeatCfg(reading);
  const stops = cfg.inks.map((ink, i) => {
    const c = `rgba(${channels(ink).join(', ')}, ${cfg.alphas[i]})`;
    return `${c} ${i * 25}%, ${c} ${(i + 1) * 25}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/**
 * A fleet's hold as text, every commodity named — holds mix since v5, so this
 * is a list and not a single figure. An empty hold still states the capacity,
 * because "empty" on its own does not tell you what the hauler could carry.
 */
export function holdText(hold: { resource: number; amount: number }[], cap: number): string {
  const parts = hold.filter((h) => h.amount > 0)
    .map((h) => `${num(h.amount)} ${RESOURCE_NAMES[h.resource] ?? '?'}`);
  if (parts.length === 0) return S.hold_empty({ cap: num(cap) });
  return S.hold_parts({ parts: parts.join(' + '), total: num(holdTotal(hold)), cap: num(cap) });
}

/**
 * Everything in a hold, all commodities together.
 *
 * The function came out of the fleet layer (R431). The overlay reads it,
 * and the overlay is DOM chrome, so the one renderer import it could not
 * take had to go.
 */
export function holdTotal(hold: { amount: number }[]): number {
  let t = 0;
  for (const h of hold) t += h.amount;
  return t;
}

/**
 * A class figure in u/h, world px per map hour, as world px per wall
 * minute, unrounded (R3270). A map hour lasts 3600 / `tacticalScale` wall
 * seconds. The current line of the water card rounds it to one place.
 */
export function uhPxMin(speedUh: number, tacticalScale: number): number {
  return (speedUh * tacticalScale) / 60;
}

/**
 * The SPEED figure in world px per wall minute (R2378, R2431, R3270): the
 * one unit of every speed on the screen. A hull in a trench sails at half
 * speed. The season card, the arcade card, the build card of a hull or a
 * round and the compare card read it, each with its own map clock.
 */
export function speedPxMin(speedUh: number, deep: boolean, tacticalScale: number): number {
  return Math.round(uhPxMin(deep ? speedUh / 2 : speedUh, tacticalScale));
}

/** A rendered speed in world px per wall second as world px per wall
 *  minute (R3270): the speed label of the chart. */
export function pxPerWallMin(pxPerWallSec: number): number {
  return pxPerWallSec * 60;
}

/**
 * How many rounds of one kind a hull of this class carries (R836).
 *
 * The TUBES bar of the fleet window prints `2 / 2`, so it needs the
 * magazine as well as the count the frame carries. This is a hand mirror
 * of `game.MagazineFor`: the hull's cargo divided by the round's own cargo
 * cost (numbers.md §5). Both figures ride the hello catalog, so no table is
 * copied here — only the one division, and the server still decides what a
 * fleet sails with.
 *
 * It answers 0 when either half is missing — a hull with empty tubes
 * carries no round kind at all — and the bar then prints the count alone
 * rather than inventing a maximum (R198).
 */
export function magazineFor(ship: { cargo: number } | undefined,
  round: { cargo: number } | undefined): number {
  if (!ship || !round || round.cargo === 0) return 0;
  return Math.floor(ship.cargo / round.cargo);
}

/**
 * The seaquake's tab (R430): the name of the stage and its figure; null
 * while calm.
 *
 * The drain line's tab, the cartouche and the clock's explainer card all
 * read it, so no two surfaces can name different stages (R2090): FLOOR
 * SHIFT, WAR ECONOMY, SETTLEMENT, and NEW SEASON in the card phase.
 *
 * Stages 1 and 2 count `leftSec`, the season clock. Stage 3 counts
 * `settlementLeftSec`, the WALL clock of the settlement (R2091, R2093) —
 * the season clock is already at 0 by then. In the drain it is the clock
 * to the end of the drain; in the card phase (`card`, R2092) it is the
 * clock to the roll. A world the calendar holds (`held`) names no roll,
 * so its card phase claims nothing.
 */
export function quakeTab(
  stage: number, leftSec: number, settlementLeftSec = 0, card = false, held = false,
): string | null {
  switch (stage) {
    case 1: return S.quake_tab_floor_moved({ left: dur(leftSec) });
    case 2: return S.quake_tab_war_economy({ left: dur(leftSec) });
    case 3:
      if (card && held) return null;
      return card
        ? S.quake_tab_season_over({ left: mmss(Math.ceil(settlementLeftSec)) })
        : S.quake_tab_has_won({ left: mmss(Math.ceil(settlementLeftSec)) });
    default: return null;
  }
}

/**
 * The seaquake's line in the clock's explainer card (R1749): the words of
 * `quakeTab` in the tone of the stage; null while calm.
 *
 * It read from the top bar until R218 and from the banner lane until
 * R430. The line names the stage by its one name (R2090) and never by
 * the word SEAQUAKE, which names the whole arc.
 */
export function quakeLabel(
  stage: number, leftSec: number, settlementLeftSec = 0, card = false, held = false,
): string | null {
  const text = quakeTab(stage, leftSec, settlementLeftSec, card, held);
  if (text === null) return null;
  // Stage 1 is a schedule and stages 2 and 3 are faults, the rule §8
  // states and `quakeClaim` keeps.
  const tone = stage === 1 ? 'hero-ink' : 'bad';
  return `<span class="season" style="color:var(--${tone})">${text}</span>`;
}

/**
 * The three claims on the timer line, as parts (R824).
 *
 * `ui-map-interface.md` §8 states the claim order and `Overlay` keeps it.
 * These three helpers only say what ONE claim is worth, and they are pure,
 * so the parts every notices style draws are pinned by a test and not by a
 * screenshot. `sentence` is what the AUTO style writes, so that value keeps
 * the tab it has had since R430.
 *
 * The founding truce (plan §5.2): a schedule, so the tone is the light one
 * of the hero ink, never gold (R1749, R1798). It has
 * no claim at all once the countdown reaches zero.
 */
export function truceClaim(leftSec: number): TimerClaim | null {
  if (!(leftSec > 0)) return null;
  const figure = dur(leftSec);
  return {
    fault: false,
    glyph: N.glyph_truce,
    label: N.timer_truce_label,
    short: N.timer_truce_short,
    figure,
    tail: N.timer_truce_tail,
    sentence: OS.truce_tab({ left: figure }),
  };
}

/**
 * The countdown of the idle sea view (R2865, ui-map-interface.md §13 "SEA
 * VIEW"): a schedule, so the tone is the light one, like the truce. It
 * counts WALL seconds, and the figure is a span rounded up (R2741), so the
 * line never reads 0s while a second remains. At zero the claim stays and
 * reads `0s`: the map waits there for the sea view to be ready.
 */
export function seaIdleClaim(leftSec: number): TimerClaim {
  const figure = leftSpan(Math.max(0, leftSec));
  return {
    fault: false,
    glyph: N.glyph_seaview,
    label: N.timer_seaview_label,
    short: N.timer_seaview_short,
    figure,
    tail: N.timer_seaview_tail,
    sentence: N.timer_seaview_sentence({ left: figure }),
  };
}

/**
 * The seaquake, in its three stages (numbers.md §15). Stage 1 is a
 * schedule and stages 2 and 3 are faults, which is the rule §8 states.
 * Stages 1 and 2 count the season clock and stage 3 counts the
 * settlement's own wall clock: the drain, then the roll in the card phase
 * (R2092), exactly as `quakeTab` does.
 */
export function quakeClaim(
  stage: number, leftSec: number, settlementLeftSec = 0, card = false, held = false,
): TimerClaim | null {
  const sentence = quakeTab(stage, leftSec, settlementLeftSec, card, held);
  if (sentence === null) return null;
  // The card phase is no stage: its clock counts the wall seconds to the
  // roll, so it names the new season (R2092). Every stage has one name
  // (R2090), so a look that shows the label or the short form names the
  // stage the tab names.
  if (stage === 3 && card) {
    return {
      fault: true,
      glyph: N.glyph_quake,
      label: N.timer_card_label,
      short: N.timer_card_short,
      figure: mmss(Math.ceil(settlementLeftSec)),
      tail: N.timer_card_tail,
      sentence,
    };
  }
  const name = stageName(stage);
  return {
    fault: stage >= 2,
    glyph: N.glyph_quake,
    label: name,
    short: name,
    figure: stage === 3 ? mmss(Math.ceil(settlementLeftSec)) : dur(leftSec),
    tail: N.timer_quake_tail({ stage }),
    sentence,
  };
}

/**
 * The seaquake's words on the cartouche (R2112): the one name of the stage
 * with no clock, so the season, the day and the stage stand on one line.
 * The card phase reads NEW SEASON, and a held world and a calm sea read
 * nothing. The top bar keeps the clock.
 */
export function quakeState(stage: number, card = false, held = false): string | null {
  if (stage === 3 && card) return held ? null : N.timer_card_label;
  return stageName(stage) || null;
}

/** The one name of a seaquake stage (R2090): FLOOR SHIFT, WAR ECONOMY or
 *  SETTLEMENT; empty while calm. */
function stageName(stage: number): string {
  switch (stage) {
    case 1: return N.timer_floor_shift;
    case 2: return N.timer_war_economy;
    case 3: return N.timer_settlement;
    default: return '';
  }
}

/**
 * The link (R108): always a fault, and never a claim while the socket is
 * up. A client that has stopped trying, and a client inside the last half
 * second before an attempt, have no figure to count — so the figure is
 * empty and the reason carries the whole answer.
 */
export function linkClaim(
  connected: boolean, retryAt: number | null, now: number,
): TimerClaim | null {
  const sentence = linkBannerText(connected, retryAt, now);
  if (sentence === '') return null;
  const left = linkWait(retryAt, now);
  const counting = left >= 0.5;
  return {
    fault: true,
    glyph: N.glyph_link,
    label: N.timer_link_label,
    short: N.timer_link_short,
    figure: counting ? `${Math.ceil(left)}s` : '',
    tail: left < 0 ? S.link_not_retrying
      : counting ? N.timer_link_tail : S.link_reconnecting,
    sentence,
  };
}

/**
 * The maintenance window ahead (deployment.md §6.1, §8). It is a SCHEDULE
 * and so the tone is the light one, like the founding truce: the worlds are still
 * running and nothing is broken yet.
 *
 * It outranks every other claim on the line, the link included. A socket
 * that drops inside a window IS the window and not a fault, and a player
 * who reads "CONNECTION LOST" there is told the wrong thing.
 *
 * The clock is the WALL clock, and `startsAt` and `now` are both unix
 * seconds. Every other claim on this line counts a world clock, because
 * every other one measures a thing the world owns; a window is an
 * operator's instant, and every world of the fleet reaches it together.
 *
 * `tail` is deliberately empty. The composed sentence names the reason and
 * the figure together, and the other three claims put their reason in a
 * tail of their own; this clock has one reason and no stages, so a tail
 * would say the word twice.
 */
export function maintenanceClaim(startsAt: number, now: number): TimerClaim | null {
  if (!(startsAt > 0)) return null;
  const left = Math.max(0, startsAt - now);
  const figure = dur(left);
  return {
    fault: false,
    glyph: N.glyph_maintenance,
    label: N.timer_maintenance_label,
    short: N.timer_maintenance_short,
    figure,
    tail: '',
    sentence: N.timer_maintenance_sentence({ left: figure }),
  };
}

/**
 * The season countdown, `28d 00:00`.
 *
 * It counts in *game* days on the world clock (R93): a fresh 28-day season
 * reads `28d 00:00` on every world, and a higher `-timescale` makes it run
 * down faster instead of shortening it. The wall-clock equivalent is the
 * secondary reading and lives in the top bar's tooltip.
 *
 * Two readers state this figure — the top bar and the season banner
 * (`ui/banner.ts`) — so the rule lives here and neither of them owns a copy
 * of it. The banner in particular must re-label the figure the top bar
 * already has and must never compute a second one.
 */
export function seasonCountdown(leftSec: number): string {
  const s = wholeSec(leftSec);
  return `${Math.floor(s / 86400)}d ${pad2(Math.floor(s / 3600) % 24)}:${pad2(Math.floor(s / 60) % 60)}`;
}

/**
 * The link banner's text: what a player is told when the world stops moving
 * (R108). Empty string while connected, so the caller's change guard hides
 * the banner on the same test it uses for every other one.
 *
 * `retryAt` is a `performance.now()`-scale stamp for the next attempt, or
 * null. **Null while disconnected means the client has stopped trying** — a
 * protocol refusal disables reconnect — and the banner has to say that rather
 * than count down to an attempt that is never coming.
 */
export function linkBannerText(connected: boolean, retryAt: number | null, now: number): string {
  if (connected) return '';
  return `<span class="mark">${S.link_lost}</span> · ` + linkBannerTail(retryAt, now);
}

/**
 * The same banner as plain text, for a surface that escapes what it is
 * handed.
 *
 * The arcade lane writes its line with `esc`, so the markup variant above
 * painted the tag itself on the glass (`arcade/chrome.ts`). A caller must
 * never be asked to trust the string with `innerHTML` to fix that, so the
 * one sentence is composed twice from one tail and the mark is dropped.
 */
export function linkBannerPlain(connected: boolean, retryAt: number | null, now: number): string {
  if (connected) return '';
  return `${S.link_lost} · ` + linkBannerTail(retryAt, now);
}

/** Seconds to the next attempt, never negative, or -1 when the client has
 *  stopped trying. The banner tail and the link claim read one figure. */
function linkWait(retryAt: number | null, now: number): number {
  return retryAt === null ? -1 : Math.max(0, (retryAt - now) / 1000);
}

/** What follows the mark in both banners. */
function linkBannerTail(retryAt: number | null, now: number): string {
  const left = linkWait(retryAt, now);
  if (left < 0) return S.link_not_retrying;
  // Under half a second there is nothing useful to count, and "retrying in
  // 0s" reads like a stuck clock.
  return left < 0.5 ? S.link_reconnecting : S.link_retry_in({ sec: Math.ceil(left) });
}

/**
 * The anchor button's label: one lever, four states, and the label says
 * which — re-labelling whichever countdown the server sent rather than
 * computing one (the panel rule: no formulas in the chrome).
 *
 * The settled state has no deadline: no anchor lifts on its own (R2858),
 * so a SET hull reads "WEIGH ANCHOR". The beam order of a Beacon has its
 * own row and its own countdown (`ui/cards/beam.ts`).
 *
 * The anchor cycle is paid on the map clock, and the label reads it in
 * world time like every other duration on screen (R591). The state is a
 * parameter, because the conversion is one factor of two clocks and the
 * chrome must not write either one as a literal.
 */
export function anchorLabel(a: {
  anchor: AnchorState; anchorLeftSec: number;
}, s: GameState): string {
  switch (a.anchor) {
    case AnchorState.ANCHOR_STATE_SETTING:
      return S.anchor_setting({ left: dur(s.mapToWorldSec(a.anchorLeftSec)) });
    case AnchorState.ANCHOR_STATE_WEIGHING:
      return S.anchor_weighing({ left: dur(s.mapToWorldSec(a.anchorLeftSec)) });
    case AnchorState.ANCHOR_STATE_SET:
      return S.anchor_weigh;
    default:
      return S.anchor_idle;
  }
}

/**
 * Does this hull take the ANCHOR order (R114, R661, R712)?
 *
 * The **server** answers, in one flag on the ship spec. Three hulls carry it:
 * the **Beacon**, which anchors to radiate sovereignty; the **Sentinel**,
 * whose set anchor buys a longer gun range; and the **Tender**, which plants
 * a berth and mounts no gun at all. On a world that latched the flag
 * `currents` the server sets it on every row (R2695): the plain anchor of
 * every other hull holds it against the current, so the client reads no
 * feature flag for the order.
 *
 * The rule sits beside `anchorLabel` because both modes ask it: the season
 * map's fleet panel and the arcade match's chip row. Neither may name a hull
 * class of its own, and neither reads a range to decide, so a fourth anchor
 * hull needs no client change — only the flag in the pack.
 */
export function canAnchor(spec: { anchors: boolean } | undefined): boolean {
  return spec?.anchors ?? false;
}

/**
 * The gun range a hull fires at right now, in world px (numbers.md §3, §4).
 *
 * A planted tower — a hull with an `anchorRange` whose anchor is SET — reaches
 * further than the same hull under way (R661). Setting and weighing keep the
 * mobile figure, because the reach exists only while the anchor is set.
 *
 * DIG IN (numbers.md §4.1, R2379, R2394) adds `digInReachPx` of the
 * catalog rules while the order holds on a class whose order it is, as
 * `Fleet.GunRange` of the sim does. `orderActive` is the fleet's
 * `signature_active`; `digInReachPx` is 0 until the Hello lands.
 *
 * The client works nothing out here: every figure is the server's own
 * catalog, and the state is the one the server sent on the fleet.
 */
export function gunRange(
  anchor: AnchorState,
  spec: { range: number; anchorRange: number; signature?: SignatureOrder } | undefined,
  orderActive = false, digInReachPx = 0,
): number {
  if (!spec) return 0;
  if (anchor === AnchorState.ANCHOR_STATE_SET && spec.anchorRange > 0) {
    return spec.anchorRange;
  }
  if (orderActive && spec.signature === SignatureOrder.SIGNATURE_ORDER_DIG_IN) {
    return spec.range + digInReachPx;
  }
  return spec.range;
}

/** The two figures of `shotText`, built once: the panels that print them
 *  rebuild four times a second, and a formatter is dear to build. */
const SHOT_DAMAGE = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 });
const SHOT_PERIOD = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1, maximumFractionDigits: 2,
});

/** The damage of one shot, from the catalog's tenths: `14.4`. The hull
 *  card writes the gain of a print with it (R1840). */
export function shotDamageText(x10: number): string {
  return SHOT_DAMAGE.format(x10 / 10);
}

/**
 * The gun's shot of a hull, as the build card and the Shipyard row print it
 * (numbers.md §4, R1135, R1136): "20 a shot, every 2.0 combat s".
 *
 * Both figures are the server's own catalog: the damage of one shot of the
 * printed hull and its fire period in combat seconds. The client multiplies
 * nothing. The combat pace never goes on the wire, so the period stays in
 * combat seconds. An unarmed hull answers the empty string.
 */
export function shotText(
  spec: { firePeriodSec: number; shotDamageX10: number } | undefined,
): string {
  if (!spec || spec.firePeriodSec <= 0 || spec.shotDamageX10 <= 0) return '';
  return OS.shot_every({
    dmg: shotDamageText(spec.shotDamageX10),
    sec: SHOT_PERIOD.format(spec.firePeriodSec),
  });
}

/**
 * A pattern's weapon and shield systems, named from the hello catalog (§17).
 *
 * It lived in overlay.ts until the arcade match tooltip had to write the same
 * pair for a contact a scan reveal has opened up (R412). The arcade bundle
 * does not carry the Overlay class, and both modes must name the systems the
 * same way, so the helper sits here beside `holdText` — the other formatter
 * the two modes share.
 *
 * The names are the server's own table, indexed by the wire's enum value: the
 * client mirrors no table of its own. Index 0 is the unspecified slot.
 */
export function typeText(s: GameState, w: number, sh: number): string {
  if (!w && !sh) return S.types_untyped;
  const wn = s.catalog?.weaponTypes?.[w] ?? '—';
  const sn = s.catalog?.shieldTypes?.[sh] ?? '—';
  return `${wn} / ${sn}`;
}

/**
 * The injury risk of one dive, as a percent figure, or `null` when the dive
 * cannot hurt the captain at all (numbers.md §11, R137, R669).
 *
 * The hello catalog carries one injury figure for the whole world: the base
 * risk of the depth. Two things move it, and both are the viewer's own, so
 * the client composes the true figure rather than printing the base:
 *
 *     risk = base × (1 − radCut × Dive Control level − proCut × Prospecting)
 *
 * The two cuts add inside one factor (R673), so the risk falls but never
 * reaches zero: the server always rolls. The two coefficients ride the
 * catalog rules, because the pack owns them.
 *
 * The figure is a whole percent, as the core chance beside it is, with one
 * exception: a risk under 1% keeps one decimal, and the tenth is floored at
 * 0.1. A rounded 0 would promise a dive the server still rolls for. The
 * function returns null for a risk of 0 or less, which the pack's own
 * guard makes impossible; the caller then states the safe dive in words.
 */
export function diveRiskPct(
  basePct: number,
  radLevel: number,
  prospecting: number,
  rules: { radRiskCutPerLevel: number; prospectingInjuryCutPerPoint: number },
): string | null {
  const risk = (basePct / 100)
    * (1 - rules.radRiskCutPerLevel * radLevel
      - rules.prospectingInjuryCutPerPoint * prospecting);
  if (risk <= 0) return null;
  const pct = risk * 100;
  if (pct >= 1) return String(Math.round(pct));
  return String(Math.max(1, Math.round(pct * 10)) / 10);
}

// --- one quantity, one idiom (mockup round 083 card 07) -------------------
//
// Every figure of a card takes one form. A part and its whole join with a
// slash and no space ("784/960"), a thin space groups the thousands
// (R687), a unit stands once after the figure, and the map unit is px
// everywhere. The figure helpers write markup: the part of a figure that
// is not the value itself (the whole, the percent sign) stands in a
// <small> so the value reads first.

/** A part of a capped whole: `784/960`. */
export function ratioFig(n: number, max: number): string {
  return `${groupThousands(n)}<small>/${groupThousands(max)}</small>`;
}

/** A share, clamped to 0 to 1, as the percent a width or a custom
 *  property takes: `42.5%`. */
export function fillPct(frac: number): string {
  return `${(clamp01(frac) * 100).toFixed(1)}%`;
}

/** A whole percent: `90%`. */
export function pctFig(pct: number): string {
  return `${Math.round(pct)}<small>%</small>`;
}

/** A distance on the map: `150 px`. */
export function pxFig(n: number): string {
  return S.unit_px({ n: groupThousands(n) });
}

/** A rate an hour: `+312/h`. */
export function rateFig(n: number): string {
  return S.unit_rate({ n: (n >= 0 ? '+' : '−') + groupThousands(Math.abs(n)) });
}

/** A held count: `×12`. */
export function heldFig(n: number): string {
  return S.unit_held({ n: groupThousands(n) });
}

/** An estimate: `≈ 1 200`. */
export function approxFig(n: number): string {
  return S.unit_approx({ n: groupThousands(n) });
}

/** A figure with at most `places` decimal places: the whole part grouped
 *  by the thin space (R687) and no trailing zero, `1 234.5`, `0.3`, `12`.
 *  Plain text, for a sentence of the string tables. */
export function placesFig(n: number, places: number): string {
  const v = Number.isFinite(n) ? n : 0;
  const [whole, frac = ''] = Math.abs(v).toFixed(places).split('.');
  const tail = frac.replace(/0+$/, '');
  const body = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_MARK) + (tail ? `.${tail}` : '');
  return v < 0 && body !== '0' ? `-${body}` : body;
}

/** Fleet hours lifted on the lanes (R2646, R3247): one place at most, so
 *  the half hour of a lane credit shows and a season total reads whole. */
export function fleetHoursFig(hours: number): string {
  return placesFig(hours, 1);
}

/** Captain practice points (numbers.md §12, R3247): two places at most.
 *  A commission pays them and a monster kill pays a share of them. */
export function practiceFig(points: number): string {
  return placesFig(points, 2);
}

/** A change: `150 → 190`. */
export function changeFig(now: string, after: string): string {
  return S.unit_change({ now, after });
}
